package com.partejar.app.feature.auth.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.partejar.app.feature.auth.data.repository.AuthRepository
import com.partejar.app.feature.auth.data.dto.AuthRequest
import com.partejar.app.feature.auth.data.dto.RegisterRequest
import com.partejar.app.feature.auth.data.repository.RegistrationException
import com.partejar.app.feature.auth.data.repository.AccountDeletionPendingLoginException
import com.partejar.app.feature.auth.state.AuthState
import com.partejar.app.core.session.SessionCoordinator
import com.partejar.app.core.security.AppLockCoordinator
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class AuthViewModel(
    private val repository: AuthRepository,
    private val sessionCoordinator: SessionCoordinator,
    private val appLockCoordinator: AppLockCoordinator? = null
) : ViewModel() {

    private val _state = MutableStateFlow(AuthState())
    val state: StateFlow<AuthState> = _state.asStateFlow()

    init {
        verificarSessaoAtiva()
    }

    fun onAction(action: AuthAction) {
        when (action) {
            is AuthAction.OnNomeChanged -> _state.update { it.copy(nome = action.nome) }
            is AuthAction.OnEmailChanged -> _state.update { it.copy(email = action.email) }
            is AuthAction.OnSenhaChanged -> _state.update { it.copy(senha = action.senha) }
            is AuthAction.OnConfirmacaoSenhaChanged -> _state.update { it.copy(confirmacaoSenha = action.confirmacao) }
            is AuthAction.OnLoginClicked -> realizarLogin()
            is AuthAction.OnRegisterClicked -> realizarCadastro()
            is AuthAction.OnRegistrationModeToggled -> _state.update { it.copy(isRegistration = !it.isRegistration, erro = null) }
            is AuthAction.OnErrorDismissed -> _state.update { it.copy(erro = null) }
        }
    }

    private fun verificarSessaoAtiva() {
        // Sessões persistidas são resolvidas pelo Splash para evitar duas fontes de navegação.
    }

    private fun realizarLogin() {
        val emailAtual = _state.value.email
        val senhaAtual = _state.value.senha

        if (emailAtual.isBlank() || senhaAtual.isBlank()) {
            _state.update { it.copy(erro = "Preencha todos os campos.") }
            return
        }

        _state.update { it.copy(isLoading = true, erro = null) }

        viewModelScope.launch {
            try {
                repository.realizarLogin(AuthRequest(emailAtual, senhaAtual))
                val destination = sessionCoordinator.resolve()
                if (destination != com.partejar.app.core.session.SessionDestination.FIRST_WORKSPACE) {
                    appLockCoordinator?.onSessionAuthenticated()
                }
                _state.update { it.copy(isLoading = false, destination = destination) }
            } catch (_: AccountDeletionPendingLoginException) {
                sessionCoordinator.clearLocalSession()
                _state.update { it.copy(isLoading = false, erro = "Esta conta tem uma solicitação de exclusão em andamento.") }
            } catch (_: Exception) {
                sessionCoordinator.clearLocalSession()
                _state.update { it.copy(isLoading = false, erro = "Falha na autenticação. Verifique suas credenciais.") }
            }
        }
    }

    private fun realizarCadastro() {
        val current = _state.value
        registrationValidationError(current.nome, current.email, current.senha, current.confirmacaoSenha)?.let { error ->
            _state.update { it.copy(erro = error) }
            return
        }

        _state.update { it.copy(isLoading = true, erro = null) }
        viewModelScope.launch {
            try {
                repository.realizarCadastro(RegisterRequest(current.nome.trim(), current.email.trim(), current.senha))
                val destination = sessionCoordinator.resolve()
                if (destination != com.partejar.app.core.session.SessionDestination.FIRST_WORKSPACE) {
                    appLockCoordinator?.onSessionAuthenticated()
                }
                _state.update { it.copy(isLoading = false, destination = destination) }
            } catch (error: RegistrationException) {
                _state.update { it.copy(isLoading = false, erro = error.messageForUi()) }
            } catch (_: Exception) {
                _state.update { it.copy(isLoading = false, erro = "Não foi possível criar sua conta. Tente novamente.") }
            }
        }
    }
}

internal fun registrationValidationError(nome: String, email: String, senha: String, confirmacao: String): String? = when {
    nome.isBlank() || email.isBlank() || senha.isBlank() || confirmacao.isBlank() -> "Preencha todos os campos."
    senha != confirmacao -> "As senhas não coincidem."
    else -> null
}

private fun RegistrationException.messageForUi(): String = when (this) {
    RegistrationException.EmailAlreadyRegistered -> "Já existe uma conta com este e-mail."
    RegistrationException.InvalidData -> "Revise seus dados e tente novamente."
    RegistrationException.Unavailable -> "Não foi possível criar sua conta. Tente novamente."
}
